import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, openAuthedBlob, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pagination from "@/components/ventrix/Pagination";
import { formatMoney, formatDateShort, StatusChip, EmptyState } from "@/components/ventrix/atoms";
import { FileText, Printer, Wallet, Mail, Plus, Search, Trash2 } from "lucide-react";

const LIMIT = 50;

export default function Invoices({ section = "admin" }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState("");         // "<id>:<action>"
  const [payFor, setPayFor] = useState(null);   // invoice open in the record-payment dialog
  const [creating, setCreating] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersReady, setCustomersReady] = useState(false);
  const [fromQuote, setFromQuote] = useState(null);   // quote to prefill the create form
  const loc = useLocation();
  const nav = useNavigate();

  const load = () => {
    setItems(null);
    api.get(`/v1/invoices?limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [offset]); // eslint-disable-line
  useEffect(() => {
    api.get("/v1/customers").then(({ data }) => setCustomers(data.items || [])).catch(() => {})
      .finally(() => setCustomersReady(true));
  }, []);

  // Arriving from "Convert to invoice": capture the quote and consume the nav state.
  useEffect(() => {
    if (loc.state?.fromQuote) {
      setFromQuote(loc.state.fromQuote);
      nav(loc.pathname, { replace: true, state: {} });
    }
  }, [loc.state]); // eslint-disable-line
  // Open the create form prefilled once the customer list is loaded (so we can
  // match the quote email to an account).
  useEffect(() => { if (fromQuote && customersReady) setCreating(true); }, [fromQuote, customersReady]);
  const prefill = useMemo(
    () => (fromQuote && customersReady ? quotePrefill(fromQuote, customers) : null),
    [fromQuote, customersReady, customers]);

  const openBlankCreate = () => { setFromQuote(null); setCreating(true); };
  const closeCreate = () => { setCreating(false); setFromQuote(null); };

  const print = async (inv) => {
    setBusy(`${inv.id}:print`);
    try { await openAuthedBlob(`/v1/invoices/${inv.id}/pdf`); }
    catch { toast.error("Could not open the invoice PDF."); }
    finally { setBusy(""); }
  };

  const sendEmail = async (inv) => {
    setBusy(`${inv.id}:send`);
    try {
      const { data } = await api.post(`/v1/invoices/${inv.id}/send`);
      if (data.sent) toast.success(`Invoice emailed to ${data.to}`);
      else toast.error(data.message || "The invoice couldn't be emailed.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <PortalLayout section={section}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="vtx-eyebrow">/ {section === "ops" ? "Ops" : "Admin"} · Billing</div>
          <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter inline-flex items-center gap-2"><FileText size={24} /> Invoices</h1>
          <p className="text-sm text-[#475569] mt-1">Every invoice across all accounts. Print, email it to the customer, or record a payment (in full or in installments).</p>
        </div>
        <button className="vtx-btn-primary inline-flex items-center gap-2" onClick={openBlankCreate} data-testid="new-invoice"><Plus size={14} /> New invoice</button>
      </div>

      <div className="vtx-card mt-6 overflow-x-auto">
        {items === null && <div className="p-6 space-y-2">{[1, 2, 3].map(i => <div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && items.length === 0 && (
          <EmptyState icon={FileText} title="No invoices yet" hint="Invoices appear here once billing is issued for shipments or quotes." />
        )}
        {items && items.length > 0 && (
          <table className="vtx-table w-full">
            <thead><tr><th>Invoice</th><th>Bill to</th><th>Amount</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {items.map(i => {
                const paid = Number(i.amount_paid) || 0;
                const bal = Math.max(0, Number(i.amount) - paid);
                const isPaid = i.status === "paid";
                return (
                  <tr key={i.id} data-testid={`invoice-row-${i.invoice_number}`}>
                    <td className="font-mono font-semibold text-[#0E2F76]">{i.invoice_number}</td>
                    <td className="text-xs text-[#475569]">{i.account_id || i.billing_email || "—"}</td>
                    <td className="font-semibold">{formatMoney(i.amount, i.currency)}
                      {!isPaid && paid > 0 && (
                        <div className="text-[10px] font-normal text-[#475569]">Paid {formatMoney(paid, i.currency)} · Bal {formatMoney(bal, i.currency)}</div>
                      )}
                    </td>
                    <td className="font-mono text-xs">{formatDateShort(i.due_date)}</td>
                    <td><StatusChip status={i.status} /></td>
                    <td>
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {!isPaid && (
                          <button onClick={() => setPayFor(i)} disabled={busy.startsWith(i.id)}
                            className="inline-flex items-center gap-1 text-xs text-[#166534] hover:underline font-bold disabled:opacity-40"
                            data-testid={`invoice-pay-${i.invoice_number}`}><Wallet size={13} /> Record payment</button>
                        )}
                        <button onClick={() => sendEmail(i)} disabled={busy.startsWith(i.id)}
                          className="inline-flex items-center gap-1 text-xs text-[#0E2F76] hover:underline font-semibold disabled:opacity-40"
                          data-testid={`invoice-send-${i.invoice_number}`}><Mail size={13} /> {busy === `${i.id}:send` ? "Sending…" : "Send"}</button>
                        <button onClick={() => print(i)} disabled={busy.startsWith(i.id)}
                          className="inline-flex items-center gap-1 text-xs text-[#0E2F76] hover:underline font-semibold disabled:opacity-40"
                          data-testid={`invoice-print-${i.invoice_number}`}><Printer size={13} /> {busy === `${i.id}:print` ? "Opening…" : "Print"}</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>

      <RecordPaymentDialog invoice={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />
      <CreateInvoiceDialog open={creating} customers={customers} prefill={prefill} onClose={closeCreate}
        onCreated={() => { closeCreate(); setOffset(0); load(); }} />
    </PortalLayout>
  );
}

// Build create-form field values from a quote request (mirrors the backend's
// quote→invoice line items: one line for a staff-set total, else itemised
// freight + surcharges). Matches the quote email to a customer account.
function quotePrefill(q, customers) {
  const c = q.computed || {};
  const cur = c.currency || "USD";
  const rows = [];
  if (q.sent_total != null) {
    rows.push({ description: "Freight & logistics services", amount: String(q.sent_total) });
  } else {
    if (c.subtotal != null) rows.push({ description: "Freight services", amount: String(c.subtotal) });
    const s = c.surcharges || {};
    if (s.fuel) rows.push({ description: "Fuel surcharge", amount: String(s.fuel) });
    if (s.security) rows.push({ description: "Security surcharge", amount: String(s.security) });
    if (rows.length === 0 && c.total != null) rows.push({ description: "Freight & logistics services", amount: String(c.total) });
  }
  const email = (q.email || "").toLowerCase();
  const match = (customers || []).find((x) => (x.email || "").toLowerCase() === email);
  return {
    mode: match ? "account" : "walkin",
    accountId: match ? match.account_id : "",
    billingName: "",
    billingEmail: match ? "" : (q.email || ""),
    currency: cur,
    reference: `Quote ${String(q.id || "").slice(-8).toUpperCase()}`,
    dueDate: "",
    rows: rows.length ? rows : [{ description: "", amount: "" }],
    quoteRequestId: q.id || null,
  };
}

function CreateInvoiceDialog({ open, customers, prefill, onClose, onCreated }) {
  const [mode, setMode] = useState("account");   // "account" | "walkin"
  const [accountId, setAccountId] = useState("");
  const [custQuery, setCustQuery] = useState("");
  const [billingName, setBillingName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [reference, setReference] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [rows, setRows] = useState([{ description: "", amount: "" }]);
  const [quoteRequestId, setQuoteRequestId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const p = prefill;
    setMode(p?.mode || "account");
    setAccountId(p?.accountId || "");
    setCustQuery("");
    setBillingName(p?.billingName || "");
    setBillingEmail(p?.billingEmail || "");
    setCurrency(p?.currency || "USD");
    setReference(p?.reference || "");
    setDueDate(p?.dueDate || "");
    setRows(p?.rows?.length ? p.rows.map((r) => ({ ...r })) : [{ description: "", amount: "" }]);
    setQuoteRequestId(p?.quoteRequestId || null);
  }, [open]); // eslint-disable-line

  const selected = customers.find((c) => c.account_id === accountId) || null;
  const matches = (() => {
    const q = custQuery.trim().toLowerCase();
    const pool = q ? customers.filter((c) => `${c.name || ""} ${c.company || ""} ${c.email || ""}`.toLowerCase().includes(q)) : customers;
    return pool.slice(0, 8);
  })();

  const subtotal = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const setRow = (i, patch) => setRows(rows.map((r, ix) => (ix === i ? { ...r, ...patch } : r)));
  const payerOk = mode === "account" ? !!accountId : !!billingEmail.trim();
  const valid = payerOk && subtotal > 0;

  const create = async () => {
    setSaving(true);
    try {
      const line_items = rows
        .filter((r) => (Number(r.amount) || 0) > 0)
        .map((r) => ({ description: r.description.trim() || "Service", amount: Number(r.amount) }));
      const payload = {
        amount: Number(subtotal.toFixed(2)),
        currency,
        reference: reference.trim() || null,
        line_items,
        due_date: dueDate ? new Date(dueDate + "T00:00:00Z").toISOString() : null,
        quote_request_id: quoteRequestId,
        ...(mode === "account"
          ? { account_id: accountId }
          : { billing_email: billingEmail.trim(), billing_name: billingName.trim() || null }),
      };
      const { data } = await api.post("/v1/invoices", payload);
      toast.success(`Invoice ${data.invoice?.invoice_number || ""} created`);
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-lg" data-testid="create-invoice-dialog">
        <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>

        <div className="flex gap-1.5">
          {[["account", "Existing customer"], ["walkin", "Walk-in (email)"]].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setMode(v)} data-testid={`inv-mode-${v}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${mode === v ? "bg-[#0E2F76] text-white" : "bg-white text-[#0E2F76] border border-[#A9C0E0]/60"}`}
              style={{ borderRadius: 2 }}>{label}</button>
          ))}
        </div>

        {mode === "account" ? (
          selected ? (
            <div className="flex items-center gap-2">
              <span className="vtx-chip" style={{ backgroundColor: "#EEF3FB", color: "#0E2F76" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#0E2F76" }} />
                {selected.name}{selected.company ? ` · ${selected.company}` : ""} ({selected.email})
              </span>
              <button type="button" className="text-xs text-[#d41f26] font-bold hover:underline" onClick={() => { setAccountId(""); setCustQuery(""); }}>Change</button>
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
                <input className="vtx-input pl-9" placeholder="Search customers by name, company or email…"
                  value={custQuery} onChange={(e) => setCustQuery(e.target.value)} data-testid="inv-customer-search" autoComplete="off" />
              </div>
              {custQuery.trim() && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#A9C0E0]/60 shadow-lg max-h-56 overflow-auto" style={{ borderRadius: 2 }}>
                  {matches.length === 0 ? <div className="px-3 py-3 text-sm text-[#475569]">No matching customers.</div>
                    : matches.map((c) => (
                      <button type="button" key={c.id} onClick={() => { setAccountId(c.account_id); setCustQuery(""); }}
                        data-testid={`inv-customer-opt-${c.account_id}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#F4FEFF] border-b border-[#A9C0E0]/20 last:border-0">
                        <div className="text-sm font-semibold text-[#0E2F76]">{c.name}{c.company ? ` · ${c.company}` : ""}</div>
                        <div className="text-xs text-[#475569] font-mono">{c.email}</div>
                      </button>
                    ))}
                </div>
              )}
            </div>
          )
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Bill-to name</span>
              <input className="vtx-input" value={billingName} onChange={(e) => setBillingName(e.target.value)} placeholder="Customer name" data-testid="inv-billing-name" /></label>
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Billing email</span>
              <input type="email" className="vtx-input" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} placeholder="customer@company.com" data-testid="inv-billing-email" /></label>
          </div>
        )}

        <div>
          <div className="vtx-eyebrow mb-1.5">Line items (amounts are pre-VAT)</div>
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex gap-2">
                <input className="vtx-input flex-1" placeholder="Description" value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} data-testid={`inv-desc-${i}`} />
                <input type="number" min="0" step="0.01" className="vtx-input w-32" placeholder="0.00" value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} data-testid={`inv-amount-${i}`} />
                {rows.length > 1 && (
                  <button type="button" className="text-[#d41f26]" onClick={() => setRows(rows.filter((_, ix) => ix !== i))}><Trash2 size={15} /></button>
                )}
              </div>
            ))}
          </div>
          <button type="button" className="mt-2 text-xs text-[#0E2F76] font-semibold hover:underline" onClick={() => setRows([...rows, { description: "", amount: "" }])} data-testid="inv-add-line">+ Add line item</button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className="block"><span className="vtx-eyebrow block mb-1.5">Currency</span>
            <input className="vtx-input" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} data-testid="inv-currency" /></label>
          <label className="block"><span className="vtx-eyebrow block mb-1.5">Reference</span>
            <input className="vtx-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="optional" data-testid="inv-reference" /></label>
          <label className="block"><span className="vtx-eyebrow block mb-1.5">Due date</span>
            <input type="date" className="vtx-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="inv-due" /></label>
        </div>

        <div className="text-sm text-[#475569]">Subtotal <span className="font-semibold text-[#0E2F76]">{formatMoney(subtotal, currency)}</span> — VAT is added on top per your company settings.</div>

        <DialogFooter>
          <button className="vtx-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="vtx-btn-primary" onClick={create} disabled={saving || !valid} data-testid="inv-create-confirm">
            {saving ? "Creating…" : "Create invoice"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordPaymentDialog({ invoice, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  const bal = invoice ? Math.max(0, Number(invoice.amount) - (Number(invoice.amount_paid) || 0)) : 0;
  useEffect(() => {
    if (invoice) { setAmount(String(bal.toFixed(2))); setReference(""); }
  }, [invoice]); // eslint-disable-line

  if (!invoice) return null;
  const cur = invoice.currency || "USD";
  const amt = Number(amount);
  const invalid = !(amt > 0) || amt - bal > 0.005;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/v1/payments", {
        invoice_id: invoice.id, amount: amt, method: "manual", reference: reference.trim(),
      });
      toast.success(data.invoice_status === "paid" ? "Invoice marked paid" : `Payment recorded · balance ${formatMoney(Number(invoice.amount) - Number(data.amount_paid), cur)}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-md" data-testid="record-payment-dialog">
        <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
        <div className="vtx-card p-4 bg-[#F4FEFF] text-sm text-[#334155]">
          <div className="font-mono font-semibold text-[#0E2F76]">{invoice.invoice_number}</div>
          <div className="mt-1 text-xs text-[#475569]">Total {formatMoney(invoice.amount, cur)} · Outstanding <span className="font-semibold text-[#0E2F76]">{formatMoney(bal, cur)}</span></div>
        </div>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Amount ({cur})</span>
          <input type="number" min="0" step="0.01" className="vtx-input" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" />
          <span className="text-[11px] text-[#475569]">Full balance marks the invoice paid; a smaller amount records an installment.</span>
        </label>
        <label className="block">
          <span className="vtx-eyebrow block mb-1.5">Reference (bank / M-Pesa txn)</span>
          <input className="vtx-input font-mono" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. FT2609ABCD" data-testid="pay-reference" />
        </label>
        <DialogFooter>
          <button className="vtx-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="vtx-btn-primary" onClick={save} disabled={saving || invalid || !reference.trim()} data-testid="pay-confirm">
            {saving ? "Recording…" : "Record payment"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
